import React, { ReactNode } from 'react';
import { Icon, useAccordionContext } from '@ver-uds/uswds-react';
import classNames from 'classnames';
import NavItems from './NavItems';
import { useParentNavIds } from './NavIdContext';

export interface NavAccordionProps {
  id: string;
  title: ReactNode;
  children: ReactNode;
  icon?: NavAccordionIcons;
  current?: boolean;
}

type NavAccordionIcons = 'user';

const IconMap = ({
  iconName = undefined,
  className,
}: {
  iconName?: NavAccordionIcons;
  className?: string;
}): JSX.Element | null => {
  switch (iconName) {
    case 'user':
      return <Icon name="account_circle" className={className} size={3} />;
    // no default
  }
  return null;
};

/**
 * The NavAccordion/GlobalHeader.Menu is currently internal to the GlobalHeader component.
 *
 * The NavAccordion provides expandable content to navigation containers.
 * When used in the GlobalHeader (as GlobalHeader.Menu) it displays as a dropdown for desktop
 * views and an expandable list for mobile views.
 */
const NavAccordion = ({ id, title, children, current = false, icon }: NavAccordionProps): JSX.Element => {
  const { isOpen, toggle } = useAccordionContext();
  const parentIds = useParentNavIds();
  const parentId = parentIds[0];
  const showMenu = isOpen(id);
  return (
    <>
      <button
        type="button"
        aria-expanded={showMenu}
        aria-controls={id}
        className={classNames({ 'ver-nav__menu-button': true, 'usa-current': current })}
        data-parent-id={parentId}
        onClick={(): void => toggle(id, parentIds)}
      >
        <IconMap iconName={icon} className="margin-right-1" />
        {title}
        <div className="ver-nav__spacer" />
        <Icon name={showMenu ? 'expand_less' : 'expand_more'} size={3} />
      </button>
      <NavItems id={id} parentIds={parentIds} hidden={!showMenu}>
        {children}
      </NavItems>
    </>
  );
};

export default NavAccordion;
